import { useState } from 'react';
import { Layers } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePartnerAccounts } from '@/api/partner-accounts';
import { useGroups } from '@/api/groups';
import { DuplicateGroupCard } from './DuplicateGroupCard';

export function DuplicateGroupsTab(): JSX.Element {
  const [accountId, setAccountId] = useState<string | null>(null);

  const { data: partnerAccountsData, isLoading: loadingAccounts } = usePartnerAccounts();
  const partnerAccounts = partnerAccountsData ?? [];

  const numericAccountId = accountId != null ? Number(accountId) : null;
  const { data: groups, isLoading } = useGroups(numericAccountId);

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <Select
          value={accountId ?? undefined}
          onValueChange={setAccountId}
          disabled={loadingAccounts}
        >
          <SelectTrigger className="w-[240px]" data-testid="group-account-filter">
            <SelectValue placeholder="Select a partner account" />
          </SelectTrigger>
          <SelectContent>
            {partnerAccounts.map((account) => (
              <SelectItem key={account.id} value={account.id}>
                {account.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {numericAccountId == null ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Layers className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
            <p className="text-lg font-medium">Select a partner account</p>
            <p className="text-muted-foreground">
              Choose a partner account to review its multi-unit duplicate groups.
            </p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="py-8 text-center text-muted-foreground">Loading duplicate groups...</div>
      ) : (groups ?? []).length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Layers className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
            <p className="text-lg font-medium">No duplicate groups found</p>
            <p className="text-muted-foreground">
              This account has no multi-unit families to review.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {(groups ?? []).map((group) => (
            <DuplicateGroupCard
              key={group.groupKey}
              group={group}
              partnerAccountId={numericAccountId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
